import apiClient, { API_BASE_URL, tokenManager } from '../core';

// Roary Event Types
export type RoaryEventType =
  | 'connected'
  | 'session_started'
  | 'message'
  | 'text_delta'
  | 'thinking'
  | 'tool_start'
  | 'tool_end'
  | 'progress'
  | 'complete'
  | 'error'
  | 'done'
  | 'cancelled'
  | 'suggestions'
  | 'choose_template'
  | 'marshmallow_update'
  | 'marshmallow_depleted'
  // Agentic build pipeline events (emitted from the build_site tool).
  | 'build_plan_ready'
  | 'build_scaffold_started'
  | 'build_scaffold_completed'
  | 'build_layout_started'
  | 'build_layout_customized'
  | 'build_layout_verifying'
  | 'build_layout_verified'
  | 'build_layout_retrying'
  | 'build_layout_failed'
  | 'build_page_started'
  | 'build_page_customized'
  | 'build_page_verifying'
  | 'build_page_verified'
  | 'build_page_retrying'
  | 'build_page_failed'
  | 'build_completed';

// Payloads for the agentic build events (shape mirrors the backend emitter).
export interface BuildPlanPage {
  slug: string;
  title: string;
  intent: string;
}
export interface BuildPlanReadyData {
  plan: {
    businessName: string;
    businessType: string;
    templateName: string;
    pages: BuildPlanPage[];
  };
}
export type BuildPageStatus =
  | 'pending'
  | 'customizing'
  | 'verifying'
  | 'retrying'
  | 'verified'
  | 'failed';
export interface BuildCompletedData {
  siteId: string;
  siteName: string;
  homePageId?: string;
  summary: {
    pagesPlanned: number;
    pagesCustomized: number;
    pagesVerified: number;
    pagesRetried: number;
    pagesFailed: number;
    layoutsProcessed: number;
    layoutsVerified: number;
    layoutsFailed: number;
  };
  stopReason: string;
  pages: Array<{
    pageId: string;
    slug: string;
    title: string;
    status: BuildPageStatus;
    issues: string[];
  }>;
  layouts: Array<{
    layoutId: string;
    name: string;
    status: BuildPageStatus;
    issues: string[];
  }>;
}

export interface RoaryEvent {
  type: RoaryEventType;
  timestamp: string;
  data: unknown;
}

export type RoarySessionMode = 'site' | 'page';

export interface RoarySession {
  id: string;
  userId: string;
  siteId: string | null;
  status: 'active' | 'completed' | 'error' | 'cancelled';
  mode: RoarySessionMode;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface RoaryMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result';
  content: string;
  toolName?: string;
  toolInput?: unknown;
  toolResult?: unknown;
  createdAt: string;
}

export interface RoaryOperation {
  id: string;
  sessionId: string;
  operationType: string;
  targetType: string;
  targetId?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  inputData?: unknown;
  resultData?: unknown;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  status: string;
  message: string;
}

export interface SessionWithMessages {
  session: RoarySession;
  messages: RoaryMessage[];
}

export interface SessionFullResponse {
  session: RoarySession;
  messages: RoaryMessage[];
  operations: RoaryOperation[];
}

export interface SendMessageSyncResponse {
  success: boolean;
  error?: string;
  events: RoaryEvent[];
}

export interface MarshmallowBalance {
  remaining: number;
  total: number;
  resetDate: string;
  history: Array<{
    sessionId: string;
    burned: number;
    date: string;
  }>;
}

export interface MarshmallowUpdate {
  burned: number;
  remaining: number;
  total: number;
}

// Callback type for SSE event handling
export type RoaryEventCallback = (event: RoaryEvent) => void;

export type SeoFieldProposal = {
  tool: 'update_page_seo' | 'update_blog_post_seo';
  field: string;
  current: string;
  proposed: string;
};

export type SeoAltProposalItem = {
  componentId: string;
  itemIndex?: number;
  itemLabel?: string;
  imageUrl: string;
  current: string;
  proposed: string;
};

export type SeoAltProposal = {
  tool: 'update_component';
  items: SeoAltProposalItem[];
};

export type SeoIssueProposal = SeoFieldProposal | SeoAltProposal;

export interface SeoIssueFixResponse {
  success: boolean;
  applied: boolean;
  summary: string;
  remaining?: number;
  proposal?: SeoIssueProposal;
}

export function isAltProposal(proposal: SeoIssueProposal): proposal is SeoAltProposal {
  return proposal.tool === 'update_component';
}

export const roaryApi = {
  // Create a new Roary session.
  // mode = 'site' (default): legacy whole-site build (admin-only going forward).
  // mode = 'page': single-page build inside an existing site (siteId required).
  async createSession(
    siteId?: string,
    mode: RoarySessionMode = 'site'
  ): Promise<CreateSessionResponse> {
    const response = await apiClient.post<CreateSessionResponse>('/roary/sessions', {
      siteId,
      mode,
    });
    return response.data;
  },

  // List user's sessions
  async listSessions(limit?: number): Promise<{ sessions: RoarySession[] }> {
    const params = limit ? { limit: Math.min(limit, 100) } : {};
    const response = await apiClient.get<{ sessions: RoarySession[] }>('/roary/sessions', {
      params,
    });
    return response.data;
  },

  // Get session details
  async getSession(sessionId: string): Promise<SessionWithMessages> {
    const response = await apiClient.get<SessionWithMessages>(`/roary/sessions/${sessionId}`);
    return response.data;
  },

  // Get session with full history including operations
  async getSessionFull(sessionId: string): Promise<SessionFullResponse> {
    const response = await apiClient.get<SessionFullResponse>(`/roary/sessions/${sessionId}/full`);
    return response.data;
  },

  // Send message with SSE streaming
  sendMessage(
    sessionId: string,
    content: string,
    onEvent: RoaryEventCallback,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): () => void {
    const token = tokenManager.get();
    const abortController = new AbortController();

    // Use fetch for SSE since axios doesn't support streaming well
    const fetchSSE = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/roary/sessions/${sessionId}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ content }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            onComplete?.();
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          let currentEvent: string | null = null;
          let currentData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6);

              if (currentEvent && currentData) {
                try {
                  const parsedData = JSON.parse(currentData);
                  onEvent({
                    type: currentEvent as RoaryEventType,
                    timestamp: new Date().toISOString(),
                    data: parsedData,
                  });
                } catch {
                  // Ignore JSON parse errors for malformed data
                  console.warn('Failed to parse SSE data:', currentData);
                }
                currentEvent = null;
                currentData = '';
              }
            } else if (line === '') {
              // Empty line marks end of event
              currentEvent = null;
              currentData = '';
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          // Request was cancelled, ignore
          return;
        }
        onError?.(error as Error);
      }
    };

    fetchSSE();

    // Return cleanup function to abort the request
    return () => {
      abortController.abort();
    };
  },

  // Send message without streaming (for simpler use cases)
  async sendMessageSync(sessionId: string, content: string): Promise<SendMessageSyncResponse> {
    const response = await apiClient.post<SendMessageSyncResponse>(
      `/roary/sessions/${sessionId}/messages/sync`,
      { content }
    );
    return response.data;
  },

  // Get marshmallow balance
  async getMarshmallows(): Promise<MarshmallowBalance> {
    const response = await apiClient.get<MarshmallowBalance>('/roary/marshmallows');
    return response.data;
  },

  // Get marshmallow packs available for purchase
  async getMarshmallowPacks(): Promise<{
    packs: Array<{
      id: string;
      name: string;
      description: string;
      marshmallows: number;
      price: number;
      priceFormatted: string;
      highlighted: boolean;
    }>;
  }> {
    const response = await apiClient.get('/roary/marshmallow-packs');
    return response.data;
  },

  // Create Stripe checkout for marshmallow pack purchase
  async createMarshmallowCheckout(
    packId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ checkoutUrl: string }> {
    const response = await apiClient.post('/roary/marshmallow-packs/checkout', {
      packId,
      successUrl,
      cancelUrl,
    });
    return response.data;
  },

  // Cancel a session
  async cancelSession(sessionId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `/roary/sessions/${sessionId}/cancel`
    );
    return response.data;
  },

  buildPage(
    input: {
      siteId: string;
      pageType: 'about' | 'pricing' | 'contact' | 'services' | 'custom';
      customTitle?: string;
      purpose: string;
      addToNav: boolean;
    },
    onEvent: (event: string, data: unknown) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): () => void {
    const token = tokenManager.get();
    const abortController = new AbortController();

    const fetchSSE = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/roary/build-page`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify(input),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const raw =
            (errorData as { message?: unknown }).message ||
            (errorData as { error?: unknown }).error;
          throw new Error(typeof raw === 'string' ? raw : `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            onComplete?.();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent: string | null = null;

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              try {
                onEvent(currentEvent, JSON.parse(line.slice(6)));
              } catch {
                /* ignore parse errors */
              }
              currentEvent = null;
            } else if (line === '') {
              currentEvent = null;
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        onError?.(error as Error);
      }
    };

    fetchSSE();
    return () => abortController.abort();
  },

  // Start an SEO audit (SSE streaming)
  startSEOAudit(
    siteId: string,
    onEvent: (event: string, data: unknown) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): () => void {
    const token = tokenManager.get();
    const abortController = new AbortController();

    const fetchSSE = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/roary/seo-audit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ siteId }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            onComplete?.();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent: string | null = null;

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              try {
                onEvent(currentEvent, JSON.parse(line.slice(6)));
              } catch {
                /* ignore parse errors */
              }
              currentEvent = null;
            } else if (line === '') {
              currentEvent = null;
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        onError?.(error as Error);
      }
    };

    fetchSSE();
    return () => abortController.abort();
  },

  // Start fixing SEO issues (SSE streaming)
  startSEOFix(
    auditId: string,
    onEvent: (event: string, data: unknown) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): () => void {
    const token = tokenManager.get();
    const abortController = new AbortController();

    const fetchSSE = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/roary/seo-audit/${auditId}/fix`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            onComplete?.();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent: string | null = null;

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              try {
                onEvent(currentEvent, JSON.parse(line.slice(6)));
              } catch {
                /* ignore parse errors */
              }
              currentEvent = null;
            } else if (line === '') {
              currentEvent = null;
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        onError?.(error as Error);
      }
    };

    fetchSSE();
    return () => abortController.abort();
  },

  fixSeoIssue: async (input: {
    pageId?: string;
    blogId?: string;
    postId?: string;
    issue: { type: 'error' | 'warning' | 'info'; message: string; recommendation?: string };
  }): Promise<SeoIssueFixResponse> => {
    const response = await apiClient.post('/roary/seo-fix-issue', input, { timeout: 120000 });
    return response.data;
  },

  applySeoIssue: async (input: {
    pageId?: string;
    blogId?: string;
    postId?: string;
    issue: { type: 'error' | 'warning' | 'info'; message: string; recommendation?: string };
    value?: string;
    items?: Array<{ componentId: string; itemIndex?: number; proposed: string }>;
  }): Promise<SeoIssueFixResponse> => {
    const response = await apiClient.post('/roary/seo-apply-issue', input, { timeout: 30000 });
    return response.data;
  },
};
